import { EmbeddedMessage, WhatsApp } from 'constants/messageMediums';
import { MessageMedium } from 'modules/graphql/types';
import { EditEmailContext } from 'modules/templateEditor/EditEmail/context/EditMailContext';
import { EmailPreviewContextProvider } from 'modules/templateEditor/EditEmail/context/EmailPreviewContextProvider';
import { EmbeddedContext } from 'modules/templateEditor/EditEmbedded/context/EmbeddedContext';
import { EmbeddedPreviewContextProvider } from 'modules/templateEditor/EditEmbedded/context/EmbeddedPreviewContextProvider';
import { InAppContext } from 'modules/templateEditor/EditInApp/context/InAppContext';
import { InAppPreviewContextProvider } from 'modules/templateEditor/EditInApp/context/InAppPreviewContextProvider';
import { PushContext } from 'modules/templateEditor/EditPush/context/PushContext';
import { PushPreviewContextProvider } from 'modules/templateEditor/EditPush/context/PushPreviewContextProvider';
import { SMSContext } from 'modules/templateEditor/EditSMS/context/SMSContext';
import { SMSPreviewContextProvider } from 'modules/templateEditor/EditSMS/context/SMSPreviewContextProvider';
import { WebPushContext } from 'modules/templateEditor/EditWebPush/context/WebPushContext';
import { WebPushPreviewContextProvider } from 'modules/templateEditor/EditWebPush/context/WebPushPreviewContextProvider';
import { WhatsAppContext } from 'modules/templateEditor/EditWhatsApp/context/WhatsAppContext';
import { WhatsAppPreviewContextProvider } from 'modules/templateEditor/EditWhatsApp/context/WhatsAppPreviewContextProvider';
import { useTemplateQueryContext } from 'modules/templateEditor/context/TemplateQueryContext';
import { useMemo } from 'react';

type MediumContextType = {
  [key in MessageMedium]?: ({ children }: { children: React.ReactNode }) => JSX.Element;
};

type PreviewMediumContextType = {
  [key in MessageMedium]?: React.FC<{
    children?: React.ReactNode;
  }>;
};

type UseTemplatePreviewContext = {
  /** Only return context related to the template preview component */
  onlyPreviewContext: boolean;
};

const MEDIUM_CONTEXTS_MAPPER: MediumContextType = {
  [MessageMedium.SMS]: SMSContext,
  [MessageMedium.Email]: EditEmailContext,
  [MessageMedium.Push]: PushContext,
  [MessageMedium.WebPush]: WebPushContext,
  [MessageMedium.InApp]: InAppContext,
  [EmbeddedMessage]: EmbeddedContext,
  [WhatsApp]: WhatsAppContext,
};

const PREVIEW_MEDIUM_CONTEXTS_MAPPER: PreviewMediumContextType = {
  [MessageMedium.SMS]: SMSPreviewContextProvider,
  [MessageMedium.Email]: EmailPreviewContextProvider,
  [MessageMedium.Push]: PushPreviewContextProvider,
  [MessageMedium.WebPush]: WebPushPreviewContextProvider,
  [MessageMedium.InApp]: InAppPreviewContextProvider,
  [EmbeddedMessage]: EmbeddedPreviewContextProvider,
  [WhatsApp]: WhatsAppPreviewContextProvider,
};

export const getContext = (medium: MessageMedium, onlyPreviewContext: boolean) =>
  onlyPreviewContext ? PREVIEW_MEDIUM_CONTEXTS_MAPPER?.[medium] : MEDIUM_CONTEXTS_MAPPER?.[medium];

export const useTemplatePreviewContext = (
  { onlyPreviewContext = false }: UseTemplatePreviewContext = {} as UseTemplatePreviewContext,
) => {
  const { medium } = useTemplateQueryContext();
  const context = useMemo(() => getContext(medium as MessageMedium, onlyPreviewContext), [medium]);
  return context;
};
